import { PutCommand, UpdateCommand, DeleteCommand, GetCommand, QueryCommand } from '@aws-sdk/lib-dynamodb';
import { Upload } from '@aws-sdk/lib-storage';
import { DeleteObjectCommand, GetObjectCommand } from '@aws-sdk/client-s3';
import { getSignedUrl } from '@aws-sdk/s3-request-presigner';
import type { CognitoUserSession } from 'amazon-cognito-identity-js';

import {
  awsConfig,
  createAuthedDocumentClient,
  createAuthedS3Client,
} from '../config/aws';
import type { PhotoMetadata } from '../utils/photoProcessing';
import { generateScanId } from '../utils/s3';
import {
  generateRegionAnalysis,
  generatePlusRegionAnalysis,
  generateProRegionAnalysis,
  generateEliteRegionAnalysis,
  determineAcneType,
  mapCountsToSeverity,
  buildUserFacingSummary,
} from '../utils/acneAnalysis';
import { generateDietPlan } from '../utils/dietPlanGenerator';
import { generateDIYMasks } from '../utils/diyMaskGenerator';
import { detectSkinIssues } from '../utils/skinIssueDetection';
import {
  mapZoneCountsToRegions,
  type AcneDetectionResult,
  type AcneZoneCounts,
} from './acneDetectionService';

export type ScanStatus = 'uploaded' | 'processing' | 'complete' | 'error' | 'deleting';

export interface ScanAsset {
  key: string;
  url: string;
  thumbKey: string;
  thumbUrl: string;
  metadata: PhotoMetadata;
}

export interface UploadSlotPayload {
  slot: 'front' | 'side';
  file: File;
  thumbnail: Blob;
  metadata: PhotoMetadata;
  angle?: 'left' | 'right' | 'unspecified';
}

export interface UploadProgressEvent {
  slot: 'front' | 'side' | 'overall';
  progress: number;
  stage: 'uploading' | 'finalizing';
}

export type SkinHealthStatus = 'Glow Rising' | 'Healing' | 'Stable' | 'Needs Attention' | 'Elite Glow' | 'Glow Mode';
export type AcneSeverity = 'none' | 'very_mild' | 'mild' | 'mild_to_moderate' | 'moderate' | 'moderate_to_severe' | 'severe';

export interface RegionAcneSummary {
  region: 'forehead' | 'nose' | 'left_cheek' | 'right_cheek' | 'chin' | 'jawline';
  severity: AcneSeverity;
  activeLesionCount: number;        // inflamed pimples, papules, pustules
  comedoneCount: number;            // small clogged pores / blackheads / whiteheads
  hyperpigmentationLevel: 'none' | 'mild' | 'moderate' | 'marked';
  textureDescription: string;       // short human-friendly description
}

export interface AcneTypeBreakdown {
  inflammatory: 'none' | 'mild' | 'moderate' | 'pronounced';
  comedonal: 'none' | 'mild' | 'moderate' | 'pronounced';
  nodularLike: 'none' | 'suspected' | 'present';  // stay careful, non-diagnostic
}

export interface SkinAnalysisResult {
  overallSeverity: AcneSeverity;
  acneType: AcneTypeBreakdown;
  regions: RegionAcneSummary[];
  estimatedPH: number | null;
  textureSummary: string;
  notesForUser: string[];
  hasInflammation?: boolean; // Indicates if there's significant inflammation
}

export interface DietFoodItem {
  name: string;
  description: string;
  type?: 'meal' | 'snack'; // Distinguish between full meals and snacks
  recipe?: {
    title: string;
    ingredients: string[];
    steps: string[];
    servings?: number;
    caloriesPerServing?: number;
  };
}

export interface DietPlan {
  foodsToEat: DietFoodItem[];
  foodsToAvoid: Array<{
    name: string;
    reason: string;
  }>;
  waterIntakeGlasses: number; // Recommended daily water intake in glasses (or 16 for 1 gallon)
  waterIntakeDisplay?: string; // e.g. "1 gallon" for acne users
  waterIntakeSkinNote?: string; // Skin benefit note for acne users
}

export interface DIYMask {
  id: string;
  title: string;
  emoji: string;
  description: string;
  benefits: string[];
  ingredients: string[];
  instructions: string[];
  duration: string;
  science: string;
  tags: string[];
}

export interface ScanAnalysis {
  skinHealthIndex: number;
  skinHealthStatus: SkinHealthStatus;
  acneSeverity: AcneSeverity; // Legacy - kept for backward compatibility
  acneFocusArea: string; // Legacy - kept for backward compatibility
  textureNotes: string; // Legacy - kept for backward compatibility
  estimatedPH?: number; // AI-estimated skin pH level (4.5-6.5 range)
  confidenceLevel?: 'low' | 'medium' | 'high'; // Confidence in analysis quality
  confidenceScore?: number; // Numeric confidence score (0-100)
  recommendations: string[]; // bullet list rendered in "Recommendations"
  progressHighlights: string[]; // bullet list rendered in "Progress highlights"
  nextBestStep: string; // text shown in "Next best step"
  // New enriched analysis
  acneAnalysis?: SkinAnalysisResult;
  // Diet and mask recommendations
  dietPlan?: DietPlan;
  diyMasks?: DIYMask[];
  // Detected skin issues (IDs from skinIssues config)
  detectedSkinIssues?: string[];
  /** YOLOv8-seg + Rekognition zone mapping from Lambda (optional) */
  yoloAcneDetection?: AcneDetectionResult;
}

export interface ScanRecord {
  uid: string;
  scanId: string;
  timestamp: string;
  status: ScanStatus;
  deleteAt?: string | null;
  glowScore?: number;
  customName?: string; // User-defined custom name for the scan
  storageKeys: {
    front: string;
    frontThumb: string;
    side: string; // Legacy - kept for backward compatibility
    sideThumb: string; // Legacy - kept for backward compatibility
    leftSide?: string;
    leftSideThumb?: string;
    rightSide?: string;
    rightSideThumb?: string;
  };
  front: {
    url: string;
    thumbUrl: string;
    metadata: PhotoMetadata;
  };
  side: {
    url: string;
    thumbUrl: string;
    metadata: PhotoMetadata;
    angle: 'left' | 'right' | 'unspecified';
  };
  leftSide?: {
    url: string;
    thumbUrl: string;
    metadata: PhotoMetadata;
  };
  rightSide?: {
    url: string;
    thumbUrl: string;
    metadata: PhotoMetadata;
  };
  angles: {
    front: boolean;
    side: 'left' | 'right' | 'unspecified';
    leftSide?: boolean;
    rightSide?: boolean;
  };
  createdAt: string;
  updatedAt: string;
  hashes: {
    front: string;
    side: string; // Legacy - kept for backward compatibility
    leftSide?: string;
    rightSide?: string;
  };
  // Analysis fields (populated when status is 'complete')
  analysis?: ScanAnalysis;
}

export const createScanId = () => generateScanId();

/**
 * Generate AI analysis for a scan from the secondary GlowScore™ index, metadata, and optional YOLO.
 *
 * `glowScore` is a deterministic skin / image-quality baseline from the client — not primary acne truth.
 * When YOLO returns usable zone counts, inflammatory signal is YOLO-first; GlowScore does not drive severity.
 *
 * @param glowScore - Secondary GlowScore™ (skin / quality index from `calculateGlowScore`)
 * @param estimatedPH - Estimated pH from the GlowScore calculator (same pipeline as glowScore)
 * @param confidenceLevel - Confidence level in the analysis
 * @param confidenceScore - Numeric confidence score (0-100)
 * @param sideAngle - Side angle of the scan
 * @param previousScan - Previous scan for comparison
 * @param hasLeftSide - Whether left side photo is available
 * @param hasRightSide - Whether right side photo is available
 * @param dietaryPreference - User's dietary preference
 * @param yoloAcne - Optional YOLOv8-seg detections with Rekognition-mapped zone counts
 */
export const generateScanAnalysis = (
  glowScore: number,
  estimatedPH: number,
  confidenceLevel: 'low' | 'medium' | 'high',
  confidenceScore: number,
  sideAngle: 'left' | 'right' | 'unspecified',
  previousScan?: ScanRecord | null,
  hasLeftSide?: boolean,
  hasRightSide?: boolean,
  dietaryPreference?: 'vegetarian' | 'non-vegetarian',
  subscriptionTier?: 'plus' | 'pro' | 'elite',
  rekognitionMetadata?: {
    front?: import('../services/rekognitionService').FaceMetadata;
    side?: import('../services/rekognitionService').FaceMetadata;
  },
  yoloAcne?: AcneDetectionResult | null,
): ScanAnalysis => {
  // pH is now passed as a parameter from the centralized calculator
  // This ensures consistency - same images = same pH

  // Base regional heuristics from secondary GlowScore™ + angles (comedones / texture); YOLO may override inflamed counts.
  let regions: RegionAcneSummary[];
  if (subscriptionTier === 'elite') {
    regions = generateEliteRegionAnalysis(
      glowScore,
      hasLeftSide ?? false,
      hasRightSide ?? false
    );
  } else if (subscriptionTier === 'pro') {
    regions = generateProRegionAnalysis(
      glowScore,
      hasLeftSide ?? false,
      hasRightSide ?? false
    );
  } else if (subscriptionTier === 'plus') {
    regions = generatePlusRegionAnalysis(
      glowScore,
      hasLeftSide ?? false,
      hasRightSide ?? false
    );
  } else {
    // Fallback to standard analysis for any other tier
    regions = generateRegionAnalysis(
      glowScore,
      hasLeftSide ?? false,
      hasRightSide ?? false
    );
  }

  const yoloPrimary =
    !!(
      yoloAcne &&
      yoloAcne.success &&
      yoloAcne.face_detected &&
      yoloAcne.zone_counts &&
      Object.keys(yoloAcne.zone_counts).length > 0
    );

  if (yoloPrimary) {
    const zcRaw = yoloAcne!.zone_counts;
    const zc: AcneZoneCounts = {
      forehead: zcRaw.forehead ?? 0,
      nose: zcRaw.nose ?? 0,
      left_cheek: zcRaw.left_cheek ?? 0,
      right_cheek: zcRaw.right_cheek ?? 0,
      chin: zcRaw.chin ?? 0,
      jawline: zcRaw.jawline ?? 0,
      other: zcRaw.other ?? 0,
    };
    const byRegion = new Map(mapZoneCountsToRegions(zc).map((m) => [m.region, m]));
    regions = regions.map((r) => {
      const y = byRegion.get(r.region);
      if (!y) return r;
      return {
        ...r,
        activeLesionCount: y.activeLesionCount,
        severity: y.severity as AcneSeverity,
      };
    });
  }

  const totalInflamed = regions.reduce((sum, r) => sum + r.activeLesionCount, 0);
  const totalComedones = regions.reduce((sum, r) => sum + r.comedoneCount, 0);
  
  // Find max region severity
  const severityOrder: AcneSeverity[] = [
    'none',
    'very_mild',
    'mild',
    'mild_to_moderate',
    'moderate',
    'moderate_to_severe',
    'severe',
  ];
  const maxRegionSeverity = regions.reduce((max, r) => {
    const maxIdx = severityOrder.indexOf(max);
    const rIdx = severityOrder.indexOf(r.severity);
    return rIdx > maxIdx ? r.severity : max;
  }, 'none' as AcneSeverity);
  
  let overallSeverity = mapCountsToSeverity(
    totalInflamed,
    totalComedones,
    maxRegionSeverity
  );

  // ── HYBRID YOLO + heuristic rule ──────────────────────────────────
  // YOLOv8 is our primary signal for INFLAMMATORY acne (papules, pustules).
  // It under-detects non-inflammatory comedones (blackheads / whiteheads),
  // so when YOLO sees ZERO inflammatory lesions on a detected face but the
  // heuristic still reports comedones, we MUST NOT label the user as clear.
  // Instead we surface the result as mild comedonal congestion.
  const yoloRanOnFace = !!(
    yoloAcne && yoloAcne.success && yoloAcne.face_detected
  );
  const yoloInflamedTotal = yoloRanOnFace
    ? (yoloAcne!.total_detections ?? 0)
    : null;
  const congestionOnly =
    yoloRanOnFace && yoloInflamedTotal === 0 && totalComedones > 0;

  let hybridComedonalCongestionApplied = false;
  if (congestionOnly) {
    hybridComedonalCongestionApplied = true;
    // Severity floor: never report 'none' / 'very_mild' when comedones exist.
    if (overallSeverity === 'none' || overallSeverity === 'very_mild') {
      overallSeverity = 'mild';
    }
    console.log('[DEBUG] Acne analysis: hybrid comedonal congestion rule applied (YOLO inflamed=0, heuristic comedones>0)', {
      yoloInflamed: yoloInflamedTotal,
      heuristicComedones: totalComedones,
      finalSeverity: overallSeverity,
    });
  }

  // Also check: If all regions are mild (0-3 pimples each), cap overall severity
  const allRegionsMild = regions.every(r => 
    (r.severity === 'none' || 
     r.severity === 'very_mild' || 
     r.severity === 'mild') &&
    r.activeLesionCount <= 3
  );
  
  // If all regions are mild, overallSeverity must be "mild" or "mild_to_moderate"
  if (allRegionsMild) {
    if (overallSeverity === 'moderate' || overallSeverity === 'moderate_to_severe' || overallSeverity === 'severe') {
      // Downgrade to mild_to_moderate if total > 12, otherwise mild
      overallSeverity = totalInflamed <= 12 ? 'mild' : 'mild_to_moderate';
    }
  }

  // ── Lambda signals low YOLO confidence (borderline / few spots) ─────────────
  // Ease GlowLens overallSeverity one notch so Rekognition/heuristic pipeline
  // doesn't contradict cautious YOLO.
  const yoloUncertainGate =
    yoloPrimary && yoloAcne?.analysis_quality?.yolo_uncertain === true;
  if (
    yoloUncertainGate &&
    overallSeverity !== 'none' &&
    overallSeverity !== 'very_mild'
  ) {
    const idx = severityOrder.indexOf(overallSeverity);
    const eased = severityOrder[Math.max(0, idx - 1)] as AcneSeverity;
    if (eased !== overallSeverity) {
      console.log('[DEBUG] Acne analysis: YOLO uncertain — eased overall severity by one band', {
        before: overallSeverity,
        after: eased,
        analysisTier: yoloAcne?.analysis_quality?.tier,
      });
      overallSeverity = eased;
    }
  }

  let glowScoreSeverityAlignmentApplied = false;
  if (!yoloPrimary) {
    glowScoreSeverityAlignmentApplied = true;
    console.log('[DEBUG] Acne analysis: hybrid fallback — aligning severity bands to secondary GlowScore™', {
      glowScoreSecondaryIndex: glowScore,
    });
    // When YOLO is not primary, nudge severity toward bands implied by the secondary GlowScore™ index.
    // 76-100 = Mild/Mostly Clear, 51-75 = Mild-Mod, 26-50 = Mod, 0-25 = Severe
    // BUT: Also respect actual lesion counts - if counts are low, severity should be mild
    let expectedSeverity: AcneSeverity;
    if (glowScore >= 76) {
      // 76-100 = Mild / Mostly Clear Skin
      // If total inflamed ≤ 12 and all regions are mild, must be "mild"
      expectedSeverity = (totalInflamed <= 12 && allRegionsMild) ? 'mild' : 
                         (totalInflamed <= 12) ? 'mild' : 'mild_to_moderate';
    } else if (glowScore >= 51) {
      // 51-75 = Mild to Moderate Acne
      // If total inflamed ≤ 12 and all regions are mild, must be "mild" (not mild_to_moderate)
      // If total inflamed 13-18, can be "mild_to_moderate"
      // If total inflamed > 18, can be "moderate"
      if (totalInflamed <= 12 && allRegionsMild) {
        expectedSeverity = 'mild'; // Low count + all regions mild = definitely mild
      } else if (totalInflamed <= 18) {
        expectedSeverity = 'mild_to_moderate'; // Moderate count = mild_to_moderate
      } else {
        expectedSeverity = 'moderate'; // Higher count = moderate
      }
    } else if (glowScore >= 26) {
      // 26-50 = Moderate Acne
      // But if counts are still low (≤ 18), allow mild_to_moderate
      expectedSeverity = (totalInflamed <= 18 && allRegionsMild) ? 'mild_to_moderate' : 'moderate';
    } else {
      // 0-25 = Severe Acne
      expectedSeverity = totalInflamed <= 40 ? 'moderate_to_severe' : 'severe';
    }
    
    // If calculated severity is more than 1 level away from expected, adjust it
    // BUT: Always respect actual lesion counts - if counts are very low, don't force higher severity
    const currentIdx = severityOrder.indexOf(overallSeverity);
    const expectedIdx = severityOrder.indexOf(expectedSeverity);
    
    // Special case: If total inflamed ≤ 12 and all regions are mild, severity MUST be "mild"
    if (totalInflamed <= 12 && allRegionsMild && overallSeverity !== 'mild' && overallSeverity !== 'very_mild') {
      overallSeverity = 'mild';
    } else if (Math.abs(currentIdx - expectedIdx) > 1) {
      // Adjust to be within 1 level of expected, but don't go higher than lesion counts justify
      const adjustedIdx = expectedIdx + (currentIdx > expectedIdx ? 1 : -1);
      const adjustedSeverity = severityOrder[Math.max(0, Math.min(severityOrder.length - 1, adjustedIdx))] as AcneSeverity;
      
      // Don't force severity higher than what lesion counts justify
      if (totalInflamed <= 12 && allRegionsMild) {
        // Low counts + all mild = must be mild or very_mild
        if (adjustedSeverity === 'moderate' || adjustedSeverity === 'moderate_to_severe' || adjustedSeverity === 'severe') {
          overallSeverity = 'mild';
        } else {
          overallSeverity = adjustedSeverity;
        }
      } else {
        overallSeverity = adjustedSeverity;
      }
    }
  } else {
    console.log('[DEBUG] Acne analysis: YOLO-first mode — overallSeverity not aligned to GlowScore bands', {
      overallSeverity,
      totalInflamed,
      glowScoreSecondaryIndex: glowScore,
    });
  }
  
  const acneType = determineAcneType(regions);

  // Hybrid rule (continued): in congestion-only mode, force the acne-type
  // breakdown to reflect comedonal acne so the UI renders "mild comedonal"
  // instead of an empty type breakdown.
  if (congestionOnly) {
    if (acneType.comedonal === 'none') acneType.comedonal = 'mild';
    // YOLO is authoritative for inflammatory: if it saw zero, the breakdown
    // must say so even if the heuristic generated phantom inflammatory counts.
    acneType.inflammatory = 'none';
  }

  // Nudge secondary GlowScore™ toward lesion pattern for the displayed skin index (not acne severity truth when YOLO-primary).
  let adjustedGlowScore = glowScore;
  
  // If lesion counts are low (≤ 12) and all regions are mild, score should be higher (65-75 range)
  if (totalInflamed <= 12 && allRegionsMild && glowScore < 65) {
    // Boost score to reflect mild condition
    adjustedGlowScore = Math.min(75, glowScore + (12 - totalInflamed) * 0.5);
    console.log('[DEBUG] Adjusted GlowScore upward to match mild acne condition', {
      originalScore: glowScore,
      adjustedScore: adjustedGlowScore,
      totalInflamed,
      allRegionsMild
    });
  }
  // If lesion counts are moderate (13-30) but score is too high (> 75), adjust down
  else if (totalInflamed > 12 && totalInflamed <= 30 && glowScore > 75) {
    adjustedGlowScore = Math.max(51, glowScore - (totalInflamed - 12) * 0.8);
    console.log('[DEBUG] Adjusted GlowScore downward to match moderate acne condition', {
      originalScore: glowScore,
      adjustedScore: adjustedGlowScore,
      totalInflamed
    });
  }
  // If lesion counts are high (> 30) but score is too high (> 60), adjust down significantly
  else if (totalInflamed > 30 && glowScore > 60) {
    adjustedGlowScore = Math.max(26, glowScore - (totalInflamed - 30) * 1.2);
    console.log('[DEBUG] Adjusted GlowScore downward to match higher acne condition', {
      originalScore: glowScore,
      adjustedScore: adjustedGlowScore,
      totalInflamed
    });
  }
  
  // Use adjusted score for final analysis
  const finalGlowScore = Math.round(adjustedGlowScore);
  
  // Update skin health status based on adjusted score
  let finalSkinHealthStatus: SkinHealthStatus;
  if (finalGlowScore >= 95) {
    finalSkinHealthStatus = 'Elite Glow';
  } else if (finalGlowScore >= 85) {
    finalSkinHealthStatus = 'Glow Mode';
  } else if (finalGlowScore >= 71) {
    finalSkinHealthStatus = 'Glow Rising';
  } else if (finalGlowScore >= 50) {
    finalSkinHealthStatus = 'Stable';
  } else if (finalGlowScore >= 35) {
    finalSkinHealthStatus = 'Healing';
  } else {
    finalSkinHealthStatus = 'Needs Attention';
  }
  
  // DEBUG: Log the acne analysis result
  console.log('[DEBUG_ACNE_RESULT]', {
    originalGlowScore: glowScore,
    adjustedGlowScore: finalGlowScore,
    totalInflamed,
    totalComedones,
    maxRegionSeverity,
    overallSeverity,
    inflammatoryType: acneType.inflammatory,
    allRegionsMild,
    yoloPrimary,
    hybridComedonalCongestionApplied,
    glowScoreSeverityAlignmentApplied,
    regions: regions.map((r) => ({
      region: r.region,
      severity: r.severity,
      activeLesionCount: r.activeLesionCount,
      comedoneCount: r.comedoneCount,
    })),
  });

  // Build texture summary - encouraging wording that matches what's actually detected
  // If most of the face is smooth → say "skin texture is mostly smooth with mild unevenness"
  const textureDescriptions = regions.map((r) => r.textureDescription);
  const uniqueTextures: string[] = [];
  textureDescriptions.forEach((desc) => {
    if (!uniqueTextures.includes(desc)) {
      uniqueTextures.push(desc);
    }
  });
  
  // Count how many regions are mostly smooth vs uneven
  const smoothRegions = regions.filter(r => 
    r.textureDescription.includes('smooth') || 
    r.textureDescription.includes('clear') ||
    r.severity === 'none' || 
    r.severity === 'very_mild' ||
    (r.severity === 'mild' && r.activeLesionCount <= 4)
  ).length;
  
  // If most regions (4+) are smooth, use encouraging summary
  const textureSummary = smoothRegions >= 4
    ? (uniqueTextures.length === 1
        ? uniqueTextures[0]
        : 'Skin texture is mostly smooth with mild unevenness in small areas.')
    : (uniqueTextures.length === 1
        ? uniqueTextures[0]
        : 'Overall skin texture looks mostly smooth, with mild unevenness around some areas.');

  // Get skin condition focus from subscription tier (needed for diet plan and recommendations)
  const skinConditionFocus = subscriptionTier === 'plus' ? 'acne-oily' 
    : subscriptionTier === 'pro' ? 'expanded' 
    : subscriptionTier === 'elite' ? 'comprehensive' 
    : 'acne-oily'; // Default to acne-oily

  // Use Rekognition metadata to enhance analysis accuracy
  const frontFace = rekognitionMetadata?.front;
  const sideFace = rekognitionMetadata?.side;
  
  // Extract age range for age-specific recommendations
  const estimatedAge = frontFace?.ageRange 
    ? Math.round((frontFace.ageRange.low + frontFace.ageRange.high) / 2)
    : undefined;
  
  // Extract gender for gender-specific recommendations
  const gender = frontFace?.gender?.value;
  
  // Extract primary emotion (highest confidence)
  const primaryEmotion = frontFace?.emotions?.[0]?.type;
  const stressLevel = primaryEmotion === 'FEAR' || primaryEmotion === 'SAD' || primaryEmotion === 'ANGRY' 
    ? 'high' 
    : primaryEmotion === 'CONFUSED' || primaryEmotion === 'DISGUSTED'
    ? 'medium'
    : 'low';

  // Generate personalized diet plan first (needed for water intake in recommendations and user summary)
  // Pass skin condition focus to filter diet recommendations appropriately
  // Pass Rekognition metadata to diet plan generator for age/gender-specific recommendations
  const dietPlan = generateDietPlan(
    finalGlowScore,
    overallSeverity, 
    estimatedPH, 
    dietaryPreference, 
    subscriptionTier, 
    skinConditionFocus, 
    regions,
    {
      age: estimatedAge,
      gender,
      stressLevel,
    }
  );

  console.log('[DEBUG] Recommendations pipeline: using final display skin index', {
    finalGlowScoreForRecommendations: finalGlowScore,
    originalSecondaryGlowScore: glowScore,
  });

  // Build user-facing summary with tier-specific detail
  const baseSummary = buildUserFacingSummary({
    overallSeverity,
    acneType,
    regions,
    estimatedPH: estimatedPH ?? null,
    textureSummary,
    notesForUser: [],
  });
  
  // Enhance summary based on tier
  let userSummary = baseSummary;
  
  if (subscriptionTier === 'plus') {
    // Plus plan: Remove all technical details (active spots, clogged pores, texture, pH)
    const filteredBulletPoints = baseSummary.bulletPoints.filter(point => {
      const lowerPoint = point.toLowerCase();
      return !lowerPoint.includes('active spots') &&
             !lowerPoint.includes('active spot') &&
             !lowerPoint.includes('clogged pores') &&
             !lowerPoint.includes('clogged pore') &&
             !lowerPoint.startsWith('texture:') &&
             !lowerPoint.includes('texture:') &&
             !lowerPoint.includes('estimated skin surface ph') &&
             !lowerPoint.includes('estimated skin surface') &&
             !lowerPoint.includes('ph:') &&
             !lowerPoint.includes('skin surface ph') &&
             !lowerPoint.includes('precision lesion') &&
             !lowerPoint.includes('multi-parameter') &&
             !lowerPoint.includes('regional severity') &&
             !lowerPoint.includes('predictive pattern');
    });
    
    userSummary = {
      ...baseSummary,
      bulletPoints: filteredBulletPoints,
    };
  } else {
    // Pro/Elite plan: Completely replace with simple, clear, user-friendly text
    // Don't use baseSummary at all - create fresh, simple text from scratch
    const activeRegions = regions.filter(r => r.activeLesionCount > 0).length;
    const needsIntervention = regions.filter(r => r.severity === 'moderate' || r.severity === 'moderate_to_severe').length;
    
    // Build completely new, simple bullet points - no technical jargon, just clear facts
    const simplePoints: string[] = [];
    
    // Very simple summary of breakouts
    if (totalInflamed > 0) {
      if (totalInflamed === 1) {
        simplePoints.push('1 active breakout');
      } else if (totalInflamed <= 5) {
        simplePoints.push(`${totalInflamed} active breakouts`);
      } else {
        simplePoints.push(`${totalInflamed} active breakouts`);
      }
    } else {
      simplePoints.push('No active breakouts');
    }
    
    // Very simple summary of clogged pores
    if (totalComedones > 0) {
      if (totalComedones <= 3) {
        simplePoints.push('A few clogged pores');
      } else {
        simplePoints.push(`${totalComedones} clogged pores`);
      }
    }
    
    // Very simple treatment guidance
    if (needsIntervention > 0) {
      simplePoints.push(`${needsIntervention} area${needsIntervention > 1 ? 's' : ''} need${needsIntervention === 1 ? 's' : ''} extra care`);
    } else if (activeRegions > 0) {
      simplePoints.push('Your skin is improving');
    }
    
    // Add healing marks info if present (very simple language)
    const regionsWithHyperpigmentation = regions.filter(r => r.hyperpigmentationLevel !== 'none');
    if (regionsWithHyperpigmentation.length > 0) {
      simplePoints.push('Some healing marks from past breakouts');
    }
    
    userSummary = {
      ...baseSummary,
      bulletPoints: simplePoints,
    };
  }

  // Legacy acne severity mapping (for backward compatibility)
  let acneSeverity: AcneSeverity;
  if (overallSeverity === 'none' || overallSeverity === 'very_mild') {
    acneSeverity = 'mild';
  } else if (overallSeverity === 'mild' || overallSeverity === 'mild_to_moderate') {
    acneSeverity = 'mild_to_moderate';
  } else if (overallSeverity === 'moderate') {
    acneSeverity = 'moderate';
  } else {
    acneSeverity = 'moderate_to_severe';
  }

  // Determine focus area based on side angle
  // Ensure right-side photos are properly recognized
  let focusArea: string;
  if (sideAngle === 'left') {
    focusArea = 'left profile';
  } else if (sideAngle === 'right') {
    focusArea = 'right profile';
  } else {
    focusArea = 'T-zone & cheeks';
  }
  
  console.log('[DEBUG] generateScanAnalysis - focus area determined', {
    sideAngle,
    focusArea,
  });


  // Generate texture notes from final display skin index and pH
  let textureNotes: string;
  if (finalGlowScore >= 80) {
    textureNotes = 'smooth and even texture with healthy glow';
  } else if (finalGlowScore >= 70) {
    textureNotes = 'mostly smooth texture with minor variations';
  } else if (finalGlowScore >= 60) {
    textureNotes = 'slightly uneven texture around cheeks and T-zone';
  } else if (finalGlowScore >= 50) {
    textureNotes = 'uneven texture with visible concerns';
  } else {
    textureNotes = 'rough texture with multiple areas needing attention';
  }
  
  // Add pH context to texture notes if pH is outside ideal range
  if (estimatedPH > 5.5) {
    textureNotes += ' (pH slightly alkaline - may benefit from pH-balancing products)';
  } else if (estimatedPH < 4.8) {
    textureNotes += ' (pH slightly acidic - skin may be sensitive)';
  }

  // Enhance texture notes with Rekognition quality data (frontFace already extracted above)
  if (frontFace?.quality) {
    if (frontFace.quality.brightness !== undefined && frontFace.quality.brightness < 50) {
      textureNotes += ' (detected: suboptimal lighting may affect analysis accuracy)';
    }
    if (frontFace.quality.sharpness !== undefined && frontFace.quality.sharpness < 50) {
      textureNotes += ' (detected: image sharpness could be improved for better analysis)';
    }
  }

  // Generate recommendations based on score, tier, and skin condition focus
  // TIER-SPECIFIC DETECTION LOGIC:
  // - Plus: Acne + oily skin ONLY (90% accuracy)
  // - Pro: Expanded conditions - Acne, oily, dryness, sensitivity (95-97% accuracy)
  // - Elite: All major skin issues - Acne, oil, dryness, sensitivity, eczema, rosacea, hyperpigmentation, aging (98%+ accuracy)
  const recommendations: string[] = [];
  
  if (subscriptionTier === 'plus') {
    // Plus plan: Acne + oily skin focus only - 90% accuracy
    // Plus does NOT detect: dryness, sensitivity, hyperpigmentation, aging, eczema/rosacea (Pro/Elite only)
    if (finalGlowScore < 70) {
      recommendations.push('DIY green-tea mask twice weekly to calm redness and reduce inflammation');
      recommendations.push('Gentle exfoliation with 2% BHA every 3 nights to unclog pores and control oil');
    }
    if (finalGlowScore < 80) {
      if (dietPlan.waterIntakeDisplay) {
        recommendations.push(`Drink ${dietPlan.waterIntakeDisplay} of water every day. ${dietPlan.waterIntakeSkinNote || 'Helps keep your skin hydrated and healthy.'}`);
      } else {
        recommendations.push(`Hydrate internally: target ${dietPlan.waterIntakeGlasses} cups of water daily`);
      }
    }
    if (finalGlowScore >= 70) {
      recommendations.push('Maintain current routine - your skin is responding well');
    }
    if (finalGlowScore < 60) {
      recommendations.push('Consider consulting a dermatologist for personalized acne treatment');
    }
    recommendations.push('Use oil-free, non-comedogenic sunscreen daily to protect your progress');
    // Acne-specific recommendations only (Plus focus)
    if (overallSeverity === 'moderate' || overallSeverity === 'moderate_to_severe' || overallSeverity === 'severe') {
      recommendations.push('Focus on anti-inflammatory ingredients like niacinamide and green tea extract for acne');
      recommendations.push('Use oil-absorbing products like clay masks to control excess sebum production');
      recommendations.push('Track redness and swelling to see how your skin responds to acne treatment');
    }
    // Oily skin recommendations only (Plus focus)
    // Detected based on: low glow score (< 75) + presence of comedones or excess sebum indicators
    const hasOilySkin = finalGlowScore < 75 && (totalComedones > 3 || regions.some(r => r.comedoneCount > 1));
    if (hasOilySkin) {
      recommendations.push('Use a lightweight, oil-free moisturizer to balance skin without adding excess oil');
      recommendations.push('Consider double cleansing with an oil-based cleanser followed by a gentle foaming cleanser');
      recommendations.push('Use salicylic acid (BHA) 2-3 times per week to unclog pores and control oil production');
    }
  } else if (subscriptionTier === 'pro') {
    // Pro plan: Expanded skin conditions (dryness, sensitivity included) - 95-97% accuracy
    // Pro detects: Acne, oily skin, dryness, sensitivity
    // Pro does NOT detect: hyperpigmentation, aging, eczema/rosacea (Elite only)
    if (finalGlowScore < 70) {
      recommendations.push('Try a green tea mask 3x per week to reduce inflammation');
      recommendations.push('Use 2% BHA every 3 nights and 0.5% retinol on alternate nights for better skin turnover');
    }
    if (finalGlowScore < 80) {
      if (dietPlan.waterIntakeDisplay) {
        recommendations.push(`Drink ${dietPlan.waterIntakeDisplay} of water every day. ${dietPlan.waterIntakeSkinNote || 'Helps keep your skin hydrated and healthy.'}`);
      } else {
        recommendations.push(`Drink ${dietPlan.waterIntakeGlasses} cups of water daily to keep your skin hydrated`);
      }
    }
    if (finalGlowScore >= 70) {
      recommendations.push('Your routine is working well! Keep up the good work and continue monitoring your progress');
    }
    if (finalGlowScore < 60) {
      recommendations.push('Consider consulting with a dermatologist for personalized treatment options');
    }
    recommendations.push('Use broad-spectrum sunscreen (SPF 50+) daily to protect your skin');
    // Acne-specific recommendations
    if (overallSeverity === 'moderate' || overallSeverity === 'moderate_to_severe' || overallSeverity === 'severe') {
      recommendations.push('Use anti-inflammatory ingredients like niacinamide, green tea, and azelaic acid');
      recommendations.push('Track your breakouts to identify patterns and triggers');
      recommendations.push('Test new products on a small area first to avoid reactions');
    }
    // Oily skin detection
    if (finalGlowScore < 75) {
      recommendations.push('Control excess oil with oil-absorbing products and lightweight, non-comedogenic moisturizers');
    }
    // Expanded conditions: Dryness - GUARANTEED detection when present (Pro only)
    // Detected based on: high pH (> 5.5) indicating alkaline/dry skin OR low glow score with texture concerns
    const hasDryness = (estimatedPH && estimatedPH > 5.5) || 
                       (estimatedPH && estimatedPH > 5.3 && finalGlowScore < 70) ||
                       (finalGlowScore < 60 && regions.some(r => r.textureDescription.includes('dry') || r.textureDescription.includes('rough')));
    if (hasDryness) {
      recommendations.push('Your skin may be experiencing dryness - use hydrating serums with hyaluronic acid and ceramides');
      recommendations.push('Avoid harsh cleansers that strip natural oils - opt for gentle, hydrating formulas');
      recommendations.push('Apply a rich, emollient moisturizer morning and night to restore skin hydration');
    }
    // Expanded conditions: Sensitivity - GUARANTEED detection when present (Pro only)
    // Detected based on: low pH (< 4.8) indicating acidic/sensitive skin OR inflammation without severe acne
    const hasSensitivity = (estimatedPH && estimatedPH < 4.8) ||
                          (estimatedPH && estimatedPH < 5.0 && overallSeverity === 'mild' && totalInflamed > 0) ||
                          (overallSeverity === 'mild' && estimatedPH && estimatedPH < 5.2 && finalGlowScore < 70);
    if (hasSensitivity) {
      recommendations.push('Your skin shows signs of sensitivity - use fragrance-free, hypoallergenic products');
      recommendations.push('Avoid over-exfoliation and introduce new products gradually to prevent irritation');
      recommendations.push('Strengthen skin barrier with ceramides, niacinamide, and avoid harsh exfoliants');
    }
  } else {
    // Elite plan: All major skin issues - 98%+ accuracy
    // Elite detects: Acne, oil, dryness, sensitivity, eczema, rosacea, hyperpigmentation, aging
    if (finalGlowScore < 70) {
      recommendations.push('Try a green tea mask 3x per week to reduce inflammation');
      recommendations.push('Use 2% BHA every 3 nights and 0.5% retinol on alternate nights for better skin turnover');
    }
    if (finalGlowScore < 80) {
      if (dietPlan.waterIntakeDisplay) {
        recommendations.push(`Drink ${dietPlan.waterIntakeDisplay} of water every day. ${dietPlan.waterIntakeSkinNote || 'Helps keep your skin hydrated and healthy.'}`);
      } else {
        recommendations.push(`Drink ${dietPlan.waterIntakeGlasses} cups of water daily to keep your skin hydrated`);
      }
    }
    if (finalGlowScore >= 70) {
      recommendations.push('Your routine is working well! Keep up the good work and continue monitoring your progress');
    }
    if (finalGlowScore < 60) {
      recommendations.push('Consider consulting with a dermatologist for personalized treatment options');
    }
    recommendations.push('Use broad-spectrum sunscreen (SPF 50+) daily to protect your skin');
    // Acne-specific recommendations
    if (overallSeverity === 'moderate' || overallSeverity === 'moderate_to_severe' || overallSeverity === 'severe') {
      recommendations.push('Use anti-inflammatory ingredients like niacinamide, green tea, and azelaic acid for acne');
      recommendations.push('Track your breakouts to identify patterns and triggers');
      recommendations.push('Test new products on a small area first to avoid reactions');
    }
    // Oily skin - GUARANTEED detection when present
    // Detected based on: low glow score (< 75) + presence of comedones or excess sebum indicators
    const hasOilySkin = finalGlowScore < 75 && (totalComedones > 5 || regions.some(r => r.comedoneCount > 2));
    if (hasOilySkin) {
      recommendations.push('Control excess oil with oil-absorbing products and lightweight, non-comedogenic moisturizers');
      recommendations.push('Use salicylic acid (BHA) 2-3 times per week to unclog pores and control oil production');
      recommendations.push('Consider using a clay mask once or twice a week to absorb excess oil');
    }
    // Dryness - GUARANTEED detection when present
    // Detected based on: high pH (> 5.5) indicating alkaline/dry skin OR low glow score with texture concerns
    const hasDryness = (estimatedPH && estimatedPH > 5.5) || 
                       (estimatedPH && estimatedPH > 5.3 && finalGlowScore < 70) ||
                       (finalGlowScore < 60 && regions.some(r => r.textureDescription.includes('dry') || r.textureDescription.includes('rough')));
    if (hasDryness) {
      recommendations.push('Combat dryness with hydrating serums containing hyaluronic acid, ceramides, and peptides');
      recommendations.push('Use gentle, hydrating cleansers and avoid over-cleansing to preserve skin barrier');
      recommendations.push('Apply a rich, emollient moisturizer morning and night to restore skin hydration');
      recommendations.push('Consider using a humidifier and drinking more water to support skin hydration from within');
    }
    // Sensitivity - GUARANTEED detection when present
    // Detected based on: low pH (< 4.8) indicating acidic/sensitive skin OR inflammation without severe acne
    const hasSensitivity = (estimatedPH && estimatedPH < 4.8) ||
                          (estimatedPH && estimatedPH < 5.0 && overallSeverity === 'mild' && totalInflamed > 0) ||
                          (overallSeverity === 'mild' && estimatedPH && estimatedPH < 5.2 && finalGlowScore < 70);
    if (hasSensitivity) {
      recommendations.push('Address sensitivity with fragrance-free, hypoallergenic products and a minimal routine');
      recommendations.push('Strengthen skin barrier with ceramides, niacinamide, and avoid harsh exfoliants');
      recommendations.push('Introduce new products gradually, patch-testing first to avoid reactions');
      recommendations.push('Use products with soothing ingredients like aloe vera, centella asiatica, and chamomile');
    }
    // Hyperpigmentation (Elite only) - GUARANTEED detection when present
    const regionsWithHyperpigmentation = regions.filter(r => r.hyperpigmentationLevel !== 'none');
    if (regionsWithHyperpigmentation.length > 0) {
      recommendations.push('Target hyperpigmentation with vitamin C, niacinamide, and alpha arbutin in your routine');
      recommendations.push('Use daily sunscreen (SPF 50+) to prevent further darkening of existing marks');
      recommendations.push('Consider incorporating azelaic acid or kojic acid to help fade existing dark spots');
    }
    // Aging (Elite only) - GUARANTEED detection when conditions suggest aging concerns
    // Check for aging signs: glow score 70-85 (mature skin showing early signs) OR glow score 85+ with texture concerns
    const hasAgingConcerns = (finalGlowScore >= 70 && finalGlowScore < 85) || 
                             (finalGlowScore >= 85 && regions.some(r => r.textureDescription.includes('uneven') || r.textureDescription.includes('texture')));
    if (hasAgingConcerns) {
      recommendations.push('Incorporate anti-aging ingredients like retinol, peptides, and antioxidants to maintain skin youthfulness');
      recommendations.push('Focus on collagen-boosting ingredients like vitamin C and peptides for firmness');
      recommendations.push('Use products with hyaluronic acid and ceramides to maintain skin hydration and barrier function');
    }
    // Eczema/Rosacea considerations (Elite only) - GUARANTEED detection when conditions suggest it
    // Detected based on: low pH (< 4.8) + inflammation OR sensitivity indicators
    const hasEczemaRosaceaIndicators = (estimatedPH && estimatedPH < 4.8 && overallSeverity !== 'none') ||
                                       (estimatedPH && estimatedPH < 4.8 && regions.some(r => r.severity === 'mild' || r.severity === 'mild_to_moderate')) ||
                                       (overallSeverity !== 'none' && estimatedPH && estimatedPH < 5.0 && finalGlowScore < 60);
    if (hasEczemaRosaceaIndicators) {
      recommendations.push('If you experience eczema or rosacea symptoms, use gentle, barrier-repair focused products');
      recommendations.push('Consider consulting a dermatologist for eczema or rosacea-specific treatment if symptoms persist');
      recommendations.push('Avoid harsh exfoliants and fragranced products that may trigger flare-ups');
      recommendations.push('Use products with ceramides, niacinamide, and colloidal oatmeal to strengthen skin barrier');
    }
  }

  // Generate progress highlights based on previous scan and tier
  const progressHighlights: string[] = [];
  const previousIndex = previousScan?.analysis?.skinHealthIndex ?? previousScan?.glowScore;
  
  if (subscriptionTier === 'plus') {
    // Plus plan: Simple progress tracking (no technical details)
    if (previousScan && typeof previousIndex === 'number') {
      const delta = finalGlowScore - previousIndex;
      if (delta > 0) {
        progressHighlights.push(`Your Skin Health Index improved by ${delta.toFixed(1)} points since last scan`);
        progressHighlights.push('Your skin is looking better - keep up the good work!');
      } else if (delta < 0) {
        progressHighlights.push(`Your Skin Health Index decreased by ${Math.abs(delta).toFixed(1)} points - consider adjusting your routine`);
        progressHighlights.push('Review recent lifestyle changes that may have affected your skin');
      } else {
        progressHighlights.push('Your Skin Health Index is stable - maintain your current routine');
      }

      // Acne severity comparison
      const previousSeverity = previousScan.analysis?.acneSeverity;
      if (previousSeverity && previousSeverity !== acneSeverity) {
        const severityOrder: Record<AcneSeverity, number> = {
          'none': 0,
          'very_mild': 1,
          'mild': 2,
          'mild_to_moderate': 3,
          'moderate': 4,
          'moderate_to_severe': 5,
          'severe': 6,
        };
        const currentOrder = severityOrder[acneSeverity] ?? 3;
        const prevOrder = severityOrder[previousSeverity as AcneSeverity] ?? 3;
        if (currentOrder < prevOrder) {
          progressHighlights.push(`Great news! Your acne improved from ${previousSeverity} to ${acneSeverity}`);
        } else {
          progressHighlights.push(`Your acne severity increased from ${previousSeverity} to ${acneSeverity} - review your routine and triggers`);
        }
      }
    } else {
      progressHighlights.push("This is your baseline scan - we'll track your progress from here");
    }
    
    if (finalGlowScore >= 70) {
      progressHighlights.push('Inflammation has decreased compared to your baseline');
    }
    progressHighlights.push('For best results, scan in consistent lighting and at the same time of day');
  } else {
    // Pro/Elite plan: Clear progress tracking
    if (previousScan && typeof previousIndex === 'number') {
      const delta = finalGlowScore - previousIndex;
      if (delta > 0) {
        progressHighlights.push(`Your Skin Health Index improved by ${delta.toFixed(1)} points! Keep up the great work`);
        progressHighlights.push("Your skin's oil balance is improving across all areas");
        progressHighlights.push('Inflammation has decreased - your routine is working');
      } else if (delta < 0) {
        progressHighlights.push(`Your Skin Health Index decreased by ${Math.abs(delta).toFixed(1)} points - review your routine and lifestyle factors`);
        progressHighlights.push('Consider checking product interactions, diet, stress levels, and environmental triggers');
      } else {
        progressHighlights.push('Your Skin Health Index is stable - your current routine is maintaining good skin health');
      }

      // Acne severity comparison
      const previousSeverity = previousScan.analysis?.acneSeverity;
      if (previousSeverity && previousSeverity !== acneSeverity) {
        const severityOrder: Record<AcneSeverity, number> = {
          'none': 0,
          'very_mild': 1,
          'mild': 2,
          'mild_to_moderate': 3,
          'moderate': 4,
          'moderate_to_severe': 5,
          'severe': 6,
        };
        const currentOrder = severityOrder[acneSeverity] ?? 3;
        const prevOrder = severityOrder[previousSeverity as AcneSeverity] ?? 3;
        if (currentOrder < prevOrder) {
          progressHighlights.push(`Great news! Your acne has improved from ${previousSeverity} to ${acneSeverity}`);
          progressHighlights.push('Your treatment is working - continue with your current routine');
        } else {
          progressHighlights.push(`Your acne severity increased from ${previousSeverity} to ${acneSeverity}`);
          progressHighlights.push('Review your diet, stress levels, hormones, and product usage to identify triggers');
        }
      }
    } else {
      progressHighlights.push("This is your baseline scan - we'll track your progress from here");
    }
    
    if (finalGlowScore >= 70) {
      progressHighlights.push('Inflammation has significantly decreased compared to your baseline');
    }
    progressHighlights.push('For best results, scan in consistent lighting and at the same time of day');
  }

  // Generate next best step based on tier
  let nextBestStep: string;
  if (subscriptionTier === 'plus') {
    // Plus plan: Simple guidance
    if (!previousScan) {
      nextBestStep = 'This is your baseline scan. Scan again in 7-14 days to track your progress and see how your skin improves.';
    } else {
      const delta = previousIndex ? finalGlowScore - previousIndex : 0;
      if (delta > 0) {
        nextBestStep = 'Your routine is working! Keep it up and scan again in 7 days to continue tracking your improvements.';
      } else if (delta < 0) {
        nextBestStep = 'Consider adjusting your skincare routine. Scan again in 7 days to see if changes help.';
      } else {
        nextBestStep = 'Your skin health is stable. Continue your routine and scan again in 7 days to maintain progress.';
      }
    }
    } else {
    // Pro/Elite plan: Clear next steps
    if (!previousScan) {
      nextBestStep = 'This is your baseline scan. Scan again in 7-14 days to track your progress and see how your skin improves over time.';
    } else {
      const delta = previousIndex ? finalGlowScore - previousIndex : 0;
      if (delta > 0) {
        nextBestStep = 'Your routine is working! Keep it up and scan again in 7 days to continue tracking your improvements.';
      } else if (delta < 0) {
        nextBestStep = 'Your skin needs some attention. Review your routine and lifestyle, then scan again in 7 days to see if changes help.';
      } else {
        nextBestStep = 'Your skin health is stable and looking good. Continue your routine and scan again in 7 days to maintain progress.';
      }
    }
  }

  // Add pH-specific recommendations based on tier
  if (subscriptionTier === 'plus') {
    if (estimatedPH > 5.5) {
      recommendations.push('Your skin pH is slightly high - use a pH-balancing cleanser (pH 5.5) to restore your skin barrier');
    }
    if (estimatedPH < 4.8) {
      recommendations.push('Your skin pH is slightly low - switch to gentle, fragrance-free products to reduce sensitivity');
    }
  } else {
    if (estimatedPH > 5.5) {
      recommendations.push('Your skin pH is slightly high - use a pH-balancing cleanser (pH 5.5) and serum to restore your skin barrier');
    }
    if (estimatedPH < 4.8) {
      recommendations.push('Your skin pH is slightly low - switch to gentle, fragrance-free products to reduce sensitivity and protect your skin barrier');
    }
  }

  // Create enriched acne analysis with tier-specific detail
  // notesForUser is already set from userSummary.bulletPoints which has been filtered/simplified
  // Clean up any remaining "MOST ADVANCED" text that might have slipped through
  let notesForUser = userSummary.bulletPoints.map(note => {
    // Remove "MOST ADVANCED:" prefix if present
    return note.replace(/MOST ADVANCED:\s*/gi, '').replace(/most advanced:\s*/gi, '').trim();
  });
  
  const acneAnalysis: SkinAnalysisResult = {
    overallSeverity,
    acneType,
    regions,
    estimatedPH: estimatedPH ?? null,
    textureSummary,
    notesForUser,
    hasInflammation: acneType.inflammatory === 'moderate' || acneType.inflammatory === 'pronounced',
  };

  // Diet plan was already generated above for use in recommendations

  // Generate personalized DIY masks - filtered by skin condition focus
  const allMasks = generateDIYMasks(
    finalGlowScore,
    overallSeverity,
    estimatedPH,
    acneType.inflammatory === 'moderate' || acneType.inflammatory === 'pronounced',
    skinConditionFocus,
    regions
  );
  
  // Limit masks based on tier
  let diyMasks: DIYMask[];
  if (subscriptionTier === 'plus') {
    diyMasks = allMasks.slice(0, 10); // 10 masks for plus (5 upfront + 5 in view more)
  } else {
    diyMasks = allMasks; // Unlimited for pro+
  }

  // Build temporary analysis object for skin issue detection
  const tempAnalysis: ScanAnalysis = {
    skinHealthIndex: finalGlowScore,
    skinHealthStatus: finalSkinHealthStatus,
    acneSeverity,
    acneFocusArea: focusArea,
    textureNotes,
    estimatedPH,
    confidenceLevel,
    confidenceScore,
    recommendations,
    progressHighlights,
    nextBestStep,
    acneAnalysis,
    dietPlan,
    diyMasks,
    ...(yoloAcne && yoloAcne.success ? { yoloAcneDetection: yoloAcne } : {}),
  };

  // Detect skin issues based on analysis and plan tier
  const detectedSkinIssues = detectSkinIssues(tempAnalysis, subscriptionTier);

  return {
    skinHealthIndex: finalGlowScore, // Use adjusted score that matches acne analysis
    skinHealthStatus: finalSkinHealthStatus, // Use status based on adjusted score
    acneSeverity, // Legacy
    acneFocusArea: focusArea, // Legacy
    textureNotes, // Legacy
    estimatedPH,
    confidenceLevel,
    confidenceScore,
    recommendations,
    progressHighlights,
    nextBestStep,
    acneAnalysis, // New enriched analysis
    dietPlan, // Personalized diet recommendations
    diyMasks, // Personalized DIY mask recommendations
    detectedSkinIssues, // Detected skin issues based on plan tier
    ...(yoloAcne && yoloAcne.success ? { yoloAcneDetection: yoloAcne } : {}),
  };
};

const buildS3Url = (key: string): string => {
  if (awsConfig.s3.publicBaseUrl) {
    return `${awsConfig.s3.publicBaseUrl.replace(/\/$/, '')}/${key}`;
  }
  return `https://${awsConfig.s3.bucket}.s3.${awsConfig.s3.region}.amazonaws.com/${key}`;
};

const uploadBlobWithProgress = async (
  client: ReturnType<typeof createAuthedS3Client>,
  params: {
    bucket: string;
    key: string;
    body: Blob;
    contentType: string;
    metadata: Record<string, string | number | undefined>;
  },
  onProgress?: (progress: number) => void
) => {
  const uploader = new Upload({
    client,
    params: {
      Bucket: params.bucket,
      Key: params.key,
      Body: params.body,
      ContentType: params.contentType,
      Metadata: Object.fromEntries(
        Object.entries(params.metadata).map(([key, value]) => [key, String(value ?? '')])
      ),
    },
  });

  if (onProgress) {
    uploader.on('httpUploadProgress', (evt) => {
      if (!evt.total || evt.total === 0) return;
      const loaded = evt.loaded ?? 0;
      const percent = Math.round((loaded / evt.total) * 100);
      onProgress(percent);
    });
  }

  await uploader.done();
};

export const uploadScanAssets = async (
  session: CognitoUserSession,
  uid: string,
  scanId: string,
  slots: UploadSlotPayload[],
  onProgress?: (event: UploadProgressEvent) => void
): Promise<Record<'front' | 'side', ScanAsset> & { leftSide?: ScanAsset; rightSide?: ScanAsset }> => {
  const s3 = createAuthedS3Client(session);
  const bucket = awsConfig.s3.bucket;

  const totals: Record<'front' | 'side' | 'leftSide' | 'rightSide', number> = {
    front: 0,
    side: 0,
    leftSide: 0,
    rightSide: 0,
  };

  const assets: Partial<Record<'front' | 'side' | 'leftSide' | 'rightSide', ScanAsset>> = {};

  for (const slot of slots) {
    // For side photos, use angle-specific keys if angle is specified
    let slotKey: 'front' | 'side' | 'leftSide' | 'rightSide' = slot.slot;
    if (slot.slot === 'side' && slot.angle) {
      slotKey = slot.angle === 'left' ? 'leftSide' : 'rightSide';
    }
    
    const key = `uploads/${uid}/${scanId}/${slotKey}.jpg`;
    const thumbKey = `uploads/${uid}/${scanId}/${slotKey}_thumb.jpg`;

    const fileProgress = { current: 0 };
    const thumbProgress = { current: 0 };
    const reportSlotProgress = () => {
      const combined = Math.round((fileProgress.current + thumbProgress.current) / 2);
      totals[slotKey as keyof typeof totals] = combined;
      if (onProgress) {
        const photoCount = Object.values(totals).filter(v => v > 0).length;
        const totalProgress = Object.values(totals).reduce((sum, val) => sum + val, 0);
        const overall = photoCount > 0 ? Math.round(totalProgress / photoCount) : 0;
        onProgress({ slot: slot.slot as 'front' | 'side', progress: combined, stage: 'uploading' });
        onProgress({ slot: 'overall', progress: overall, stage: 'uploading' });
      }
    };

    await Promise.all([
      uploadBlobWithProgress(
        s3,
        {
          bucket,
          key,
          body: slot.file,
          contentType: slot.file.type || 'image/jpeg',
          metadata: {
            uid,
            scanId,
            slot: slot.slot,
            width: slot.metadata.width,
            height: slot.metadata.height,
          },
        },
        (p) => {
          fileProgress.current = p;
          reportSlotProgress();
        }
      ),
      uploadBlobWithProgress(
        s3,
        {
          bucket,
          key: thumbKey,
          body: slot.thumbnail,
          contentType: 'image/jpeg',
          metadata: {
            uid,
            scanId,
            slot: slotKey,
            type: 'thumbnail',
            width: slot.metadata.width,
            height: slot.metadata.height,
          },
        },
        (p) => {
          thumbProgress.current = p;
          reportSlotProgress();
        }
      ),
    ]);

    totals[slotKey as keyof typeof totals] = 100;
    if (onProgress) {
      const photoCount = Object.values(totals).filter((v) => v > 0).length;
      const totalProgress = Object.values(totals).reduce((sum, val) => sum + val, 0);
      const overall = photoCount > 0 ? Math.round(totalProgress / photoCount) : 0;
      onProgress({ slot: slot.slot as 'front' | 'side', progress: 100, stage: 'uploading' });
      onProgress({ slot: 'overall', progress: overall, stage: 'uploading' });
    }

    assets[slotKey as keyof typeof assets] = {
      key,
      url: buildS3Url(key),
      thumbKey,
      thumbUrl: buildS3Url(thumbKey),
      metadata: slot.metadata,
    };
  }

  if (onProgress) {
    onProgress({ slot: 'overall', progress: 100, stage: 'uploading' });
  }

  // Ensure we have at least front and one side for backward compatibility
  const result: Record<'front' | 'side', ScanAsset> & { leftSide?: ScanAsset; rightSide?: ScanAsset } = {
    front: assets.front!,
    side: assets.side || assets.leftSide || assets.rightSide!,
  };
  
  if (assets.leftSide) {
    result.leftSide = assets.leftSide;
  }
  if (assets.rightSide) {
    result.rightSide = assets.rightSide;
  }

  return result;
};

export const saveScanRecord = async (
  session: CognitoUserSession,
  record: Omit<ScanRecord, 'timestamp' | 'createdAt' | 'updatedAt'> & { createdAt?: string; updatedAt?: string }
): Promise<ScanRecord> => {
  const now = new Date().toISOString();
  const timestamp = record.createdAt ?? now;
  const docClient = createAuthedDocumentClient(session);

  const item: ScanRecord = {
    ...record,
    timestamp,
    createdAt: record.createdAt ?? now,
    updatedAt: record.updatedAt ?? now,
    deleteAt: record.deleteAt ?? null,
  };

  console.log('[DEBUG] Saving scan record', {
    userId: item.uid,
    scanId: item.scanId,
    timestamp: item.timestamp,
    status: item.status,
  });

  await docClient.send(
    new PutCommand({
      TableName: awsConfig.dynamodb.scansTable,
      Item: item,
    })
  );

  console.log('[DEBUG] Scan record saved successfully', {
    userId: item.uid,
    scanId: item.scanId,
    timestamp: item.timestamp,
  });

  return item;
};

export const updateScanRecord = async (
  session: CognitoUserSession,
  uid: string,
  timestamp: string,
  updates: Partial<Omit<ScanRecord, 'uid' | 'timestamp'>>
): Promise<void> => {
  const docClient = createAuthedDocumentClient(session);

  const updateExpressions: string[] = [];
  const expressionAttributeNames: Record<string, string> = {};
  const expressionAttributeValues: Record<string, unknown> = {};

  Object.entries(updates).forEach(([key, value], index) => {
    const attrName = `#attr${index}`;
    const attrValue = `:val${index}`;
    updateExpressions.push(`${attrName} = ${attrValue}`);
    expressionAttributeNames[attrName] = key;
    expressionAttributeValues[attrValue] = value;
  });

  expressionAttributeNames['#updatedAt'] = 'updatedAt';
  expressionAttributeValues[':updatedAt'] = new Date().toISOString();
  updateExpressions.push('#updatedAt = :updatedAt');

  console.log('[DEBUG] Updating scan record', {
    userId: uid,
    timestamp,
    updates: Object.keys(updates),
    hasAnalysis: !!updates.analysis,
    analysisData: updates.analysis ? {
      skinHealthIndex: updates.analysis.skinHealthIndex,
      skinHealthStatus: updates.analysis.skinHealthStatus,
      acneSeverity: updates.analysis.acneSeverity,
      recommendationsCount: updates.analysis.recommendations?.length ?? 0,
      progressHighlightsCount: updates.analysis.progressHighlights?.length ?? 0,
      hasNextBestStep: !!updates.analysis.nextBestStep,
    } : null,
  });

  await docClient.send(
    new UpdateCommand({
      TableName: awsConfig.dynamodb.scansTable,
      Key: { uid, timestamp },
      UpdateExpression: `SET ${updateExpressions.join(', ')}`,
      ExpressionAttributeNames: expressionAttributeNames,
      ExpressionAttributeValues: expressionAttributeValues,
    })
  );

  console.log('[DEBUG] Scan record updated successfully', {
    userId: uid,
    timestamp,
    status: updates.status,
  });
};

export const deleteScanRecord = async (
  session: CognitoUserSession,
  uid: string,
  timestamp: string
): Promise<void> => {
  const docClient = createAuthedDocumentClient(session);

  await docClient.send(
    new DeleteCommand({
      TableName: awsConfig.dynamodb.scansTable,
      Key: { uid, timestamp },
    })
  );
};

export const deleteScanAssets = async (session: CognitoUserSession, assets: ScanAsset[]): Promise<void> => {
  const s3 = createAuthedS3Client(session);
  for (const asset of assets) {
    await s3.send(
      new DeleteObjectCommand({
        Bucket: awsConfig.s3.bucket,
        Key: asset.key,
      })
    );

    await s3.send(
      new DeleteObjectCommand({
        Bucket: awsConfig.s3.bucket,
        Key: asset.thumbKey,
      })
    );
  }
};

export const getScanRecord = async (
  session: CognitoUserSession,
  uid: string,
  timestamp: string
): Promise<ScanRecord | null> => {
  const docClient = createAuthedDocumentClient(session);

  const result = await docClient.send(
    new GetCommand({
      TableName: awsConfig.dynamodb.scansTable,
      Key: { uid, timestamp },
    })
  );

  return (result.Item as ScanRecord) ?? null;
};

export const getSignedScanAssetUrl = async (
  session: CognitoUserSession,
  key: string,
  expiresIn: number = 900
): Promise<string> => {
  const s3 = createAuthedS3Client(session);
  const command = new GetObjectCommand({
    Bucket: awsConfig.s3.bucket,
    Key: key,
  });

  return getSignedUrl(s3, command, { expiresIn });
};

export interface ListUserScansResponse {
  items: ScanRecord[];
  lastEvaluatedKey?: Record<string, unknown>;
}

export const listUserScans = async (
  session: CognitoUserSession,
  uid: string,
  limit: number = 20,
  exclusiveStartKey?: Record<string, unknown>
): Promise<ListUserScansResponse> => {
  const docClient = createAuthedDocumentClient(session);

  console.log('[DEBUG] Querying scans for user', {
    userId: uid,
    limit,
    table: awsConfig.dynamodb.scansTable,
  });

  const response = await docClient.send(
    new QueryCommand({
      TableName: awsConfig.dynamodb.scansTable,
      KeyConditionExpression: '#uid = :uid',
      ExpressionAttributeNames: {
        '#uid': 'uid',
      },
      ExpressionAttributeValues: {
        ':uid': uid,
      },
      Limit: limit,
      ExclusiveStartKey: exclusiveStartKey as any,
      ScanIndexForward: false,
    })
  );

  const items = (response.Items as ScanRecord[]) ?? [];
  console.log('[DEBUG] Results fetched', {
    userId: uid,
    count: items.length,
    scanIds: items.map(item => item.scanId),
    scansWithAnalysis: items.filter(item => !!item.analysis).length,
    scansByStatus: {
      complete: items.filter(item => item.status === 'complete').length,
      processing: items.filter(item => item.status === 'processing').length,
      error: items.filter(item => item.status === 'error').length,
    },
    sampleScan: items.length > 0 ? {
      scanId: items[0].scanId,
      status: items[0].status,
      hasAnalysis: !!items[0].analysis,
      createdAt: items[0].createdAt ?? items[0].timestamp,
    } : null,
  });

  return {
    items,
    lastEvaluatedKey: response.LastEvaluatedKey as Record<string, unknown> | undefined,
  };
};

export const getScanRecordByScanId = async (
  session: CognitoUserSession,
  uid: string,
  scanId: string
): Promise<ScanRecord | null> => {
  const docClient = createAuthedDocumentClient(session);

  if (awsConfig.dynamodb.scansByScanIdIndex) {
    const response = await docClient.send(
      new QueryCommand({
        TableName: awsConfig.dynamodb.scansTable,
        IndexName: awsConfig.dynamodb.scansByScanIdIndex,
        KeyConditionExpression: '#scanId = :scanId',
        FilterExpression: '#uid = :uid',
        ExpressionAttributeNames: {
          '#scanId': 'scanId',
          '#uid': 'uid',
        },
        ExpressionAttributeValues: {
          ':scanId': scanId,
          ':uid': uid,
        },
        Limit: 1,
      })
    );

    const match = response.Items?.[0] as ScanRecord | undefined;
    if (match) {
      return match;
    }
  }

  let lastEvaluatedKey: Record<string, unknown> | undefined;
  do {
    const response = await docClient.send(
      new QueryCommand({
        TableName: awsConfig.dynamodb.scansTable,
        KeyConditionExpression: '#uid = :uid',
        ExpressionAttributeNames: {
          '#uid': 'uid',
        },
        ExpressionAttributeValues: {
          ':uid': uid,
        },
        Limit: 25,
        ExclusiveStartKey: lastEvaluatedKey as any,
        ScanIndexForward: false,
      })
    );

    const items = (response.Items as ScanRecord[]) ?? [];
    const found = items.find((item) => item.scanId === scanId);
    if (found) {
      return found;
    }

    lastEvaluatedKey = response.LastEvaluatedKey as Record<string, unknown> | undefined;
  } while (lastEvaluatedKey);

  return null;
};
